import { clsx } from "clsx";
import { Input } from "molecules/input/Input";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface StartSearchResult {
	id: string;
	label: string;
	sublabel?: string | undefined;
}

export interface StartSearchGroup {
	id: string;
	label: string;
	items: StartSearchResult[];
}

interface StartSearchViewProps {
	query: string;
	onQueryChange: (query: string) => void;
	groups: StartSearchGroup[];
	/** Source queries still in flight — show a pending row instead of "no results". */
	isLoading?: boolean;
	onSelect: (groupId: string, itemId: string) => void;
}

export const StartSearchView = ({
	query,
	onQueryChange,
	groups,
	isLoading = false,
	onSelect,
}: StartSearchViewProps) => {
	const { t } = useTranslation();
	const listboxId = useId();
	const [isFocused, setIsFocused] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);

	const isOpen = isFocused && query.trim().length > 0;
	const visibleGroups = useMemo(
		() => groups.filter((group) => group.items.length > 0),
		[groups],
	);
	// Flattened so arrow keys move over one continuous list across group headings.
	const flatItems = useMemo(
		() =>
			visibleGroups.flatMap((group) =>
				group.items.map((item) => ({ group, item })),
			),
		[visibleGroups],
	);
	const hasResults = flatItems.length > 0;
	const activeItem = flatItems[activeIndex];
	const optionId = (groupId: string, itemId: string) =>
		`${listboxId}-option-${groupId}-${itemId}`;

	const selectActive = () => {
		if (!activeItem || isLoading) {
			return;
		}
		onSelect(activeItem.group.id, activeItem.item.id);
	};

	return (
		<div className="relative">
			<Input
				role="combobox"
				aria-expanded={isOpen}
				aria-controls={listboxId}
				aria-activedescendant={
					isOpen && activeItem
						? optionId(activeItem.group.id, activeItem.item.id)
						: undefined
				}
				value={query}
				onChange={(event) => {
					onQueryChange(event.target.value);
					setActiveIndex(0);
				}}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				onKeyDown={(event) => {
					if (!hasResults) {
						if (event.key === "Escape") {
							onQueryChange("");
						}
						return;
					}
					switch (event.key) {
						case "ArrowDown":
							event.preventDefault();
							setActiveIndex((index) => (index + 1) % flatItems.length);
							break;
						case "ArrowUp":
							event.preventDefault();
							setActiveIndex(
								(index) => (index - 1 + flatItems.length) % flatItems.length,
							);
							break;
						case "Enter":
							event.preventDefault();
							selectActive();
							break;
						case "Escape":
							onQueryChange("");
							break;
						default:
							break;
					}
				}}
				placeholder={t("Search reports, companies, data sources...")}
				type="search"
			/>
			{isOpen && (
				<div
					id={listboxId}
					role="listbox"
					aria-label={t("Search reports, companies, data sources...")}
					className="absolute inset-x-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded border border-grey-300 bg-white py-1 shadow-lg"
				>
					{hasResults ? (
						visibleGroups.map((group) => (
							<div key={group.id} className="mt-6 first:mt-0">
								<p className="px-3 pb-1 text-xs font-semibold uppercase text-grey-400">
									{group.label}
								</p>
								{group.items.map((item) => {
									const index = flatItems.findIndex(
										(flat) =>
											flat.group.id === group.id && flat.item.id === item.id,
									);
									return (
										<button
											key={item.id}
											id={optionId(group.id, item.id)}
											type="button"
											role="option"
											aria-selected={index === activeIndex}
											onMouseEnter={() => setActiveIndex(index)}
											// onMouseDown, not onClick — blur closes the list before a click lands.
											onMouseDown={(event) => {
												event.preventDefault();
												onSelect(group.id, item.id);
											}}
											className={clsx(
												"flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm text-gray-700",
												index === activeIndex
													? "bg-gray-100"
													: "hover:bg-gray-100",
											)}
										>
											<span className="truncate font-medium">{item.label}</span>
											{item.sublabel && (
												<span className="shrink-0 text-xs text-grey-500">
													{item.sublabel}
												</span>
											)}
										</button>
									);
								})}
							</div>
						))
					) : (
						<p className="px-3 py-2 text-sm text-grey-500">
							{isLoading
								? t("Searching...")
								: t("No results matched your search.")}
						</p>
					)}
				</div>
			)}
		</div>
	);
};
